import { isForbiddenError } from "api/helpers";
import { ModalContent } from "components/icbt/assignIcbtModal/assignIcbt.styles";
import {
  List,
  MappedTreatmentData,
  PossiblyErrors,
} from "components/icbt/assignIcbtModal/common";
import { ProgramItemWithFeedbackDate } from "components/icbt/assignIcbtModal/feedbackDate";
import { useTranslation } from "react-i18next";
import * as R from "remeda";
import { Button, Icon, Spinner } from "@mindlercare/ui-web";
import { Typography } from "components/typography";

import { IcbtClaims } from "api/types";
import {
  CenteredContainer,
  Footer,
} from "components/icbt/assignIcbtModal/icbtSelector/icbtSelector.styles";
import { useAssignIcbt } from "../assignIcbtContext";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { styled } from "styled-components";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";

const ProgramItemList = styled(List)`
  justify-content: flex-start;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: fit-content;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.PADDING.MD};
  margin-top: ${({ theme }) => theme.PADDING.LG};
  margin-bottom: ${({ theme }) => theme.PADDING.MD};
`;

interface SelectFeedbackDatesProps {
  programsToBeAssigned: string[];
  isPreviewOnly: boolean;
  isLoading: boolean;
  setProgressStep: Dispatch<SetStateAction<number>>;
  treatments: MappedTreatmentData;
  icbtClaims?: IcbtClaims;
  programsToBeAssignedPairedWithTreatment: string[][];
  handleSelectProgram: (programIds: string[], treatmentId?: string) => void;
  handleOpenConfirm?: () => void;
  isError: boolean;
  possiblyErrors: PossiblyErrors;
  patientUserId: string;
}

export const SelectFeedbackDates = ({
  programsToBeAssigned,
  isPreviewOnly,
  isLoading,
  setProgressStep,
  treatments,
  icbtClaims,
  programsToBeAssignedPairedWithTreatment,
  handleSelectProgram,
  handleOpenConfirm,
  isError,
  possiblyErrors,
  patientUserId,
}: SelectFeedbackDatesProps) => {
  const { t } = useTranslation();
  const { state: assignIcbtState, totalCountOfProgramsToBeAssigned } =
    useAssignIcbt();
  const { programFeedbackDates } = assignIcbtState.assignState;
  const programsToBeAssignedTotalCount = totalCountOfProgramsToBeAssigned;
  const programsWithFeedbackDates = Object.keys(programFeedbackDates ?? {});

  const programsById = useMemo(() => {
    const treatmentData = Object.values(treatments.byId);
    return R.pipe(
      treatmentData,
      R.flatMap((item) => item.programs),
      R.indexBy((item) => item.id),
      R.mapValues((item) => ({
        program: item,
        index: treatments.byId[item.treatmentId]?.programs.indexOf(item) + 1,
      })),
    );
  }, [treatments]);

  const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);

  const getProgramDataById = (programId: string) => {
    return programsById[programId];
  };

  return (
    <>
      <ModalContent>
        {(() => {
          if (isError) {
            return (
              <CenteredContainer>
                <Typography insensitive variant="h3">
                  {Object.values(possiblyErrors).some(isForbiddenError)
                    ? t("ICBT_LOADING_DATA_ERROR_FORBIDDEN")
                    : t("ICBT_LOADING_DATA_ERROR")}
                </Typography>
              </CenteredContainer>
            );
          }
          if (isLoading) {
            return (
              <CenteredContainer>
                <Spinner />
              </CenteredContainer>
            );
          }
          return (
            <List>
              <HeadingContainer>
                <DescriptionContainer>
                  <Icon.Assignment
                    style={{ width: 20, color: defaultTheme.COLORS.BLACK_GREY }}
                  />
                  <Typography
                    insensitive
                    variant="h3"
                    sx={{ marginLeft: "10px" }}
                  >
                    {t("SET_ICBT_DUE_DATE_TITLE")}
                  </Typography>
                </DescriptionContainer>
                <DescriptionContainer>
                  <Typography insensitive variant="body" color="DARK_GREY">
                    {t("SELECT_ICBT_DATE_DESCRIPTION")}
                  </Typography>
                </DescriptionContainer>
              </HeadingContainer>
              <ProgramItemList>
                {programsToBeAssignedPairedWithTreatment.map(
                  ([treatmentId, programId], index) => (
                    <ProgramItemWithFeedbackDate
                      key={`program-item-with-due-date-${index}`}
                      programTitle={
                        getProgramDataById(programId)?.program?.title
                      }
                      treatmentTitle={treatments.byId[treatmentId]?.title}
                      programId={programId}
                      programIndex={getProgramDataById(programId).index}
                      onRemoveItem={() =>
                        handleSelectProgram([programId], treatmentId)
                      }
                      onDateSelectorOpen={setIsDateSelectorOpen}
                      patientUserId={patientUserId}
                    />
                  ),
                )}
              </ProgramItemList>
            </List>
          );
        })()}
      </ModalContent>
      {!isPreviewOnly ? (
        <Footer>
          <Button
            disabled={isDateSelectorOpen}
            variant="secondary"
            onClick={() => setProgressStep(0)}
            ml={8}
            testId="closeButton"
          >
            {t("ADD_ICBT")}
          </Button>
          <Button
            disabled={
              !icbtClaims?.isPatientOfPsychologist ||
              programsToBeAssignedTotalCount === 0 ||
              // Disable if all programs do not have a due date
              programsToBeAssigned
                .filter((x) => !programsWithFeedbackDates.includes(x))
                .concat(
                  programsWithFeedbackDates.filter(
                    (x) => !programsToBeAssigned.includes(x),
                  ),
                ).length != 0
            }
            variant="primary"
            onClick={handleOpenConfirm}
            testId="assignButton"
          >
            {t("ASSIGN")}
          </Button>
        </Footer>
      ) : null}
    </>
  );
};
